import { useState, useEffect } from "react"
import { CircleNotch } from "@phosphor-icons/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select"
import { MatterSearch } from "../../../components/ui/collection-search/matter-search"
import { ObjectName } from "../../../components/ui/object-name"
import { useUIStore } from "../../../stores/ui-store"
import { useStartTimer, useActiveTimer } from "../hooks/useTimer"
import { useBillingCategories } from "../hooks/useBillingCategories"
import { useTemplatesByCategory } from "../hooks/useTimeEntryTemplates"
import { toast } from "sonner"
import type { TimerStartRequest } from "../types"

interface TimerStartDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultMatterId?: string
}

export function TimerStartDialog({
  open,
  onOpenChange,
  defaultMatterId,
}: TimerStartDialogProps) {
  const selectedDivision = useUIStore((s) => s.selectedDivision)
  const startTimer = useStartTimer()
  const { data: activeTimer } = useActiveTimer()
  const { data: categories } = useBillingCategories(selectedDivision || undefined)

  const [matterId, setMatterId] = useState<string | null>(defaultMatterId || null)
  const [billingCategory, setBillingCategory] = useState("")
  const [templateId, setTemplateId] = useState("")
  const [description, setDescription] = useState("")

  const { data: templates } = useTemplatesByCategory(
    selectedDivision || undefined,
    billingCategory || undefined
  )

  useEffect(() => {
    if (open) {
      setMatterId(defaultMatterId || null)
      setBillingCategory("")
      setTemplateId("")
      setDescription("")
    }
  }, [open, defaultMatterId])

  // When a template is selected, auto-fill description if empty
  useEffect(() => {
    if (templateId && templates) {
      const tmpl = templates.find((t) => t._id === templateId)
      if (tmpl && !description) {
        setDescription(tmpl.description || tmpl.name)
      }
    }
  }, [templateId, templates, description])

  const handleStart = async () => {
    if (!matterId) {
      toast.error("Please select a matter")
      return
    }

    const data: TimerStartRequest = {
      matter: matterId,
      description: description.trim() || undefined,
      category: billingCategory || undefined,
      template: templateId || undefined,
    }

    try {
      await startTimer.mutateAsync(data)
      toast.success("Timer started")
      onOpenChange(false)
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Start Timer</DialogTitle>
          <DialogDescription>
            Start a timer for a matter. The timer will run until you stop it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Active timer warning */}
          {activeTimer && (
            <div className="rounded-md bg-warning/10 border border-warning/20 p-3 text-sm">
              <span className="font-medium">Timer running:</span>{" "}
              <ObjectName collection="matters" id={activeTimer.matter} />
              <span className="text-muted-foreground ml-1">— will be replaced</span>
            </div>
          )}

          {/* Matter */}
          <div className="space-y-2">
            <Label>Matter *</Label>
            <MatterSearch
              value={matterId}
              onChange={(opt) => setMatterId(opt?.value || null)}
              placeholder="Search matters..."
            />
          </div>

          {/* Billing Category */}
          <div className="space-y-2">
            <Label>Category</Label>
            <Select
              value={billingCategory || "none"}
              onValueChange={(v) => {
                setBillingCategory(v === "none" ? "" : v)
                setTemplateId("")
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {(categories || []).map((cat) => (
                  <SelectItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Activity Template */}
          {billingCategory && templates && templates.length > 0 && (
            <div className="space-y-2">
              <Label>Activity Template</Label>
              <Select
                value={templateId || "none"}
                onValueChange={(v) => setTemplateId(v === "none" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {templates.map((tmpl) => (
                    <SelectItem key={tmpl._id} value={tmpl._id}>
                      {tmpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What are you working on?"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleStart} disabled={startTimer.isPending}>
            {startTimer.isPending && (
              <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
            )}
            {activeTimer ? "Stop & Start New" : "Start Timer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
